/**
 * Betting calculation utilities
 * Handles payout calculations, odds conversions, and probability calculations
 */

/**
 * Calculate potential payout from bet amount and odds
 * 
 * Formula for American odds:
 * - Negative odds (-150): Bet $150 to win $100, total payout = $250
 *   Payout = Bet Amount × (100 / |Odds|) + Bet Amount
 * - Positive odds (+200): Bet $100 to win $200, total payout = $300
 *   Payout = Bet Amount × (Odds / 100) + Bet Amount
 * 
 * @param amount - Bet amount in dollars
 * @param odds - American odds (-150, +200, etc.)
 * @returns Total payout if bet wins
 */
export function calculatePayout(amount: number, odds: number): number {
  if (odds < 0) {
    // Negative odds: bet $150 to win $100
    return amount * (100 / Math.abs(odds)) + amount;
  } else {
    // Positive odds: bet $100 to win $200
    return amount * (odds / 100) + amount;
  }
}

/**
 * Calculate winnings (profit) from bet amount and odds
 * This is the payout minus the original bet amount
 * 
 * @param amount - Bet amount in dollars
 * @param odds - American odds (-150, +200, etc.)
 * @returns Profit if bet wins (payout - bet amount)
 */
export function calculateWinnings(amount: number, odds: number): number {
  return calculatePayout(amount, odds) - amount;
}

/**
 * Convert American odds to implied probability
 * 
 * Formula:
 * - Negative odds (-150): Probability = |Odds| / (|Odds| + 100)
 * - Positive odds (+200): Probability = 100 / (Odds + 100)
 * 
 * @param odds - American odds (-150, +200, etc.)
 * @returns Implied probability as a decimal (0.0 to 1.0)
 */
export function oddsToProbability(odds: number): number {
  if (odds < 0) {
    return Math.abs(odds) / (Math.abs(odds) + 100);
  } else {
    return 100 / (odds + 100);
  }
}

/**
 * Convert American odds to implied probability percentage
 * 
 * @param odds - American odds (-150, +200, etc.)
 * @returns Implied probability as a percentage (0 to 100)
 */
export function oddsToProbabilityPercent(odds: number): number {
  return oddsToProbability(odds) * 100;
}

/**
 * Format American odds for display
 * Always shows the sign (+ or -)
 * 
 * @param odds - American odds (-150, +200, etc.)
 * @returns Formatted odds string (e.g., "-150", "+200")
 */
export function formatOdds(odds: number): string {
  if (odds > 0) {
    return `+${odds}`;
  }
  return odds.toString();
}

/**
 * Format currency for display
 * 
 * @param amount - Amount in dollars
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted currency string (e.g., "$1,000.00")
 */
export function formatCurrency(amount: number, decimals: number = 2): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
}

/**
 * Parse bet amount from string input
 * Handles dollar signs, commas, and decimal points
 * 
 * @param input - User input string
 * @returns Parsed amount as number, or null if invalid
 */
export function parseBetAmount(input: string): number | null {
  // Remove dollar signs and commas
  const cleaned = input.replace(/[$,\s]/g, '');
  
  // Parse as float
  const amount = parseFloat(cleaned);
  
  // Validate
  if (isNaN(amount) || amount <= 0) {
    return null;
  }
  
  // Round to 2 decimal places
  return Math.round(amount * 100) / 100;
}

/**
 * Validate bet amount
 * 
 * @param amount - Bet amount to validate
 * @param minAmount - Minimum bet amount (default: $1.00)
 * @param maxAmount - Maximum bet amount (optional)
 * @returns True if valid, false otherwise
 */
export function validateBetAmount(
  amount: number,
  minAmount: number = 1.0,
  maxAmount?: number
): boolean {
  if (amount < minAmount) {
    return false;
  }
  
  if (maxAmount !== undefined && amount > maxAmount) {
    return false;
  }
  
  // Check for reasonable decimal places (max 2)
  const rounded = Math.round(amount * 100) / 100;
  if (Math.abs(amount - rounded) > 0.001) {
    return false;
  }
  
  return true;
}

/**
 * Calculate potential ROI (Return on Investment) from bet
 * 
 * @param amount - Bet amount in dollars
 * @param odds - American odds (-150, +200, etc.)
 * @returns ROI as a percentage
 */
export function calculateROI(amount: number, odds: number): number {
  const winnings = calculateWinnings(amount, odds);
  return (winnings / amount) * 100;
}

/**
 * Get quick bet amount suggestions
 * Common bet amounts for quick selection
 * 
 * @param availableBalance - Participant's available balance
 * @returns Array of suggested bet amounts
 */
export function getQuickBetAmounts(availableBalance: number): number[] {
  const suggestions: number[] = [];
  
  // Add common amounts if they're less than available balance
  [25, 50, 100, 250, 500].forEach((amount) => {
    if (amount <= availableBalance) {
      suggestions.push(amount);
    }
  });
  
  // Add "all in" option (available balance)
  if (availableBalance > 0) {
    suggestions.push(availableBalance);
  }
  
  return suggestions;
}












